import React, { useEffect, useState } from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import XIcon from './icons/XIcon';
import SparklesIcon from './icons/SparklesIcon';

interface RecipeTextInputModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (text: string) => void;
    initialText: string;
    language?: Language;
}

const RecipeTextInputModal: React.FC<RecipeTextInputModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initialText,
    language,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [draft, setDraft] = useState(initialText);

    useEffect(() => {
        if (isOpen) {
            setDraft(initialText);
        }
    }, [isOpen, initialText]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[640] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-white/20 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 text-brand-orange" />
                        <h3 className="font-black text-slate-800 dark:text-slate-100 tracking-tight text-lg uppercase">
                            {t('recipeTextForAi')}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 transition-colors">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 flex-grow overflow-hidden flex flex-col gap-3">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        {t('recipeTextForAiDesc')}
                    </p>
                    <div className="relative flex-grow min-h-[320px]">
                        <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder={t('pasteText')}
                            className="h-full min-h-[320px] w-full p-4 pb-12 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 text-sm leading-relaxed text-slate-700 dark:text-slate-300 shadow-inner resize-y outline-none focus:ring-2 focus:ring-brand-sage/40"
                        />
                        {draft.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setDraft('')}
                                className="absolute top-3 end-3 bg-slate-200 dark:bg-slate-700 rounded-full p-0.5 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                                aria-label={t('clearField')}
                            >
                                <XIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="p-6 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-3 text-slate-400 dark:text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onSave(draft);
                            onClose();
                        }}
                        className="flex-[2] py-3 bg-brand-sage text-white font-black rounded-xl shadow-lg shadow-brand-sage/20 hover:opacity-90 transition-all text-xs uppercase tracking-widest"
                    >
                        {t('save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecipeTextInputModal;
